import { formatDistanceToNow } from 'date-fns';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Card, ChatAltOutline, Flex, HStack, Icon, Text, VStack } from '@bonusly/design-system';

import type { BizyUserConversation } from '../../../schemas/bizy-conversations';
import { getDateFnsLocale } from '../../../utils/date';

interface ConversationCardProps {
	conversation: BizyUserConversation;
	onClick: (conversationId: string) => void;
}

export const ConversationCard: FC<ConversationCardProps> = ({ conversation, onClick }) => {
	const { t, i18n } = useTranslation('bizy_history');

	return (
		<Card
			p={4}
			cursor="pointer"
			onClick={() => onClick(conversation.conversationId)}
			_hover={{ bg: 'bg.canvas', borderColor: 'border.default' }}
			transition="all 0.15s ease"
			border="1px solid"
			borderColor="border.soft"
			role="button"
			tabIndex={0}
			onKeyDown={(e: React.KeyboardEvent) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onClick(conversation.conversationId);
				}
			}}
		>
			<VStack align="stretch" spacing={3}>
				{/* First message preview */}
				<Text fontSize="sm" color="text.default" noOfLines={2} fontWeight="medium">
					{conversation.firstUserMessage}
				</Text>

				{/* Metadata row */}
				<Flex justify="space-between" align="center" wrap="wrap" gap={2}>
					<HStack spacing={3}>
						{/* Message count */}
						<HStack spacing={1}>
							<Icon as={ChatAltOutline} boxSize={3.5} color="text.softer" />
							<Text fontSize="xs" color="text.softer">
								{t('conversation_card.message_count', { count: conversation.messageCount })}
							</Text>
						</HStack>

						{/* Page path */}
						{conversation.pagePath && (
							<Box>
								<Text fontSize="xs" color="text.softest" fontFamily="mono" noOfLines={1} maxW="200px">
									{conversation.pagePath}
								</Text>
							</Box>
						)}
					</HStack>

					{/* Time ago */}
					<Text fontSize="xs" color="text.softer">
						{formatDistanceToNow(conversation.lastMessageAt, { addSuffix: true, locale: getDateFnsLocale(i18n.language) })}
					</Text>
				</Flex>
			</VStack>
		</Card>
	);
};
